// High Valyrian phonology: IPA, syllables, weight and stress.
//
// `ipa()` is a faithful port of the wiki's Module:Hv-pronunc (used by {{hv-IPA}}), itself
// based on linguifex Module:qhv-pron. It reproduces the module's Lua-pattern rules step by
// step, including its quirks, so that `ipa(word)` equals the text between the slashes of the
// wiki's "(Classical) IPA: /…/" line. The wiki prints no syllable dots; `syllabify()` gives
// the module's syllables if you need them.
//
// Stress rule implemented by the module (and therefore here):
//   - one syllable, or a hyphenated affix: no stress mark;
//   - an acute vowel (á é í ó ú ý, from an explicit {{hv-IPA|…}} argument) marks stress manually;
//   - two syllables: penult;
//   - otherwise antepenult if the penult is light and the antepenult heavy, else penult.
//   A syllable is heavy when it ends in a consonant, a long vowel (ː) or the offglide of the
//   diphthongs ae/ao (written e̯/o̯ internally).
// Stress is recomputed from the surface form, so it moves correctly in inflected forms
// (ābra → ˈaːbra, ābrossa → aːˈbrossa).
//
// Commands are the exception the module does not know: imperatives have final stress, the only
// words with word-final stress (Peterson; lesson 01 "Käskymuodoissa paino on aina viimeisellä
// tavulla": kelīˈtīs, memēbāˈtās). The callers that know a word is a command (command-stress.ts,
// from the word's analyses) pass `final`; `ipa(word)` without it stays the wiki module's output.

const NONSYLLABIC = '̯'; // combining inverted breve below
const DIPHTHONG = '͜'; // combining double breve below

const PALATAL = 'ɲʎɟj';
const VELAR = 'kgxɣ';
const UVULAR = 'q';
const C = `[mpbvwθntdszlrɾɲŋɴʎhɥjɟṛʃ${VELAR}${UVULAR}]`;
const V = `[aeiouyáéíóúýàèìòùỳː${NONSYLLABIC}${DIPHTHONG}]`;
const VONLY = `[aeiouyáéíóúýàèìòùỳ${NONSYLLABIC}${DIPHTHONG}]`;
const BV = '[aeiouyáéíóúýàèìòùỳ]';

/** Tracked string: `t` with, per UTF-16 unit, the index of the source character it came from. */
interface Tracked {
  t: string;
  src: number[];
}

type Repl = string | ((m: RegExpExecArray) => string);

/**
 * Lua-style gsub over a tracked string. Lua 5.1 and JS global replace agree on empty-match
 * handling for the patterns used here (try at each position; after an empty match copy one
 * character). Output characters of a replacement get the source index of the matched text:
 * group references keep their own sources, literal characters take the source of the first
 * matched character (or the previous output character for an empty match).
 */
// The patterns are a fixed set of module constants, so one compiled RegExp per pattern is kept
// (tgsub is not re-entrant: no replacement callback calls it, and lastIndex is reset per call).
const RE_CACHE = new Map<string, RegExp>();

function tgsub(s: Tracked, pattern: string, repl: Repl): Tracked {
  let re = RE_CACHE.get(pattern);
  if (!re) {
    re = new RegExp(pattern, 'gdu');
    RE_CACHE.set(pattern, re);
  }
  re.lastIndex = 0;
  const outT: string[] = [];
  const outS: number[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  const push = (str: string, src: number) => {
    for (let k = 0; k < str.length; k++) {
      outT.push(str[k]!);
      outS.push(src);
    }
  };
  const copy = (from: number, to: number) => {
    for (let k = from; k < to; k++) {
      outT.push(s.t[k]!);
      outS.push(s.src[k]!);
    }
  };
  while ((m = re.exec(s.t)) !== null) {
    const start = m.index;
    const end = start + m[0].length;
    copy(last, start);
    const fallbackSrc = start < s.t.length ? s.src[start]! : (s.src[s.t.length - 1] ?? 0);
    const prevSrc = outS.length ? outS[outS.length - 1]! : fallbackSrc;
    const litSrc = m[0].length ? s.src[start]! : prevSrc;
    if (typeof repl === 'function') {
      push(repl(m), litSrc);
    } else {
      let i = 0;
      while (i < repl.length) {
        const ch = repl[i]!;
        if (ch === '%' && i + 1 < repl.length && /[0-9]/.test(repl[i + 1]!)) {
          const g = Number(repl[i + 1]);
          const val = g === 0 ? m[0] : (m[g] ?? '');
          const ind = m.indices?.[g];
          if (ind) {
            for (let k = ind[0]; k < ind[1]; k++) {
              outT.push(s.t[k]!);
              outS.push(s.src[k]!);
            }
          } else {
            push(val, litSrc);
          }
          i += 2;
        } else if (ch === '%' && repl[i + 1] === '%') {
          push('%', litSrc);
          i += 2;
        } else {
          push(ch, litSrc);
          i += 1;
        }
      }
    }
    last = end;
    if (m[0].length === 0) {
      // advance by one code point, copying it (Lua copies the char; JS skips it in exec)
      if (end >= s.t.length) break;
      const cp = s.t.codePointAt(end)!;
      const len = cp > 0xffff ? 2 : 1;
      copy(end, end + len);
      last = end + len;
      re.lastIndex = end + len;
    }
  }
  copy(last, s.t.length);
  return { t: outT.join(''), src: outS };
}

/** Plain (untracked) Lua-style gsub. */
function gsub(s: string, pattern: string, repl: Repl): string {
  return tgsub({ t: s, src: Array.from({ length: s.length }, (_, i) => i) }, pattern, repl).t;
}

const RULES: [string, string][] = [
  ['rh', 'ṛ'],
  ['mh', 'm'],
  ['th', 'θ'],
  ['lj', 'ʎ'],
  ['ñ', 'ɲ'],
  ['kh', 'x'],
  ['gh', 'ɣ'],
  ['j', 'ɟ'],
  ['sh', 'ʃ'],
  ['([aāáà][eo])', '%1' + NONSYLLABIC],
  ['([iíuú])([aāeēoōáéóàèò])', '%1' + DIPHTHONG + '%2'],
  ['ā', 'aː'],
  ['ē', 'eː'],
  ['ī', 'iː'],
  ['ō', 'oː'],
  ['ū', 'uː'],
  ['ȳ', 'yː'],
  ['à', 'áː'],
  ['è', 'éː'],
  ['ì', 'íː'],
  ['ò', 'óː'],
  ['ù', 'úː'],
  ['ỳ', 'ýː'],
  [`n([${PALATAL}])`, 'ɲ%1'],
  [`n([${VELAR}])`, 'ŋ%1'],
  [`n([${UVULAR}])`, 'ɴ%1'],
];

const DEACUTE: Record<string, string> = { á: 'a', é: 'e', í: 'i', ó: 'o', ú: 'u', ý: 'y' };

/** Apply the module's letter→phone rules (lowercase first), tracking source characters. */
function applyRules(word: string): Tracked {
  const lower = word.normalize('NFC').toLowerCase();
  let s: Tracked = { t: lower, src: Array.from({ length: lower.length }, (_, i) => i) };
  for (const [p, r] of RULES) s = tgsub(s, p, r);
  return s;
}

export type Weight = 'heavy' | 'light';

export interface StressAnalysis {
  /** Syllables in the module's internal phone notation (before final cosmetic replacements). */
  rawSyllables: string[];
  /** Syllables as printed in IPA (ṛ → r̥, offglide/linking marks removed, acutes removed). */
  ipaSyllables: string[];
  weights: Weight[];
  /** Index of the stressed syllable; null for monosyllables and hyphenated affixes (no mark). */
  stressIndex: number | null;
  /** True when the stress came from an acute accent in the input. */
  manual: boolean;
}

function splitHiatus(m: RegExpExecArray): string {
  const [, v1, v2, nonsyll] = m;
  return nonsyll === '' ? `${v1}·${v2}` : `${v1}${v2}${nonsyll}`;
}

/** The module's write_stress(), returning the syllable list and stress position. */
function writeStress(term0: string): { syllables: string[]; weights: Weight[]; stressIndex: number | null; manual: boolean } {
  let term = term0;
  term = gsub(term, `(${BV})(${BV})(${NONSYLLABIC}?)`, splitHiatus);
  term = gsub(term, `(${BV})(${BV})(${NONSYLLABIC}?)`, splitHiatus);
  term = gsub(term, `(${NONSYLLABIC})(${BV})`, '%1·%2');
  term = gsub(term, `(ː)(${BV})(${NONSYLLABIC}?)`, splitHiatus);

  term = gsub(term, `(${C}*${V}*${C}*?)`, '·%1');
  term = gsub(term, '·+', '·');
  term = gsub(term, '^·', '');
  term = gsub(term, '·$', '');
  term = gsub(term, `·(${C})·`, '·%1');
  term = gsub(term, `·(${C})$`, '%1');
  term = gsub(term, `·(${C})(${C})`, '%1·%2');
  term = gsub(term, '([ptkbdg])·([rlṛsz])', '·%1%2');
  term = gsub(term, `·(${C}?${C})$`, '%1');

  const syllables = term.split('·');
  const weights: Weight[] = [];
  if (syllables.length === 1 || term.includes('-')) {
    for (const s of syllables) weights.push(syllWeight(s));
    return { syllables, weights, stressIndex: null, manual: false };
  }
  for (let i = 0; i < syllables.length; i++) {
    const syl = syllables[i]!;
    if (/[áéíóúý]/u.test(syl)) {
      return { syllables, weights: syllables.map(syllWeight), stressIndex: i, manual: true };
    }
    weights.push(syllWeight(syl));
  }
  const n = syllables.length;
  const a = n >= 3 ? weights[n - 3] : undefined;
  const p = weights[n - 2];
  let stressIndex: number;
  if (p === undefined) stressIndex = n - 1;
  else if (a === undefined) stressIndex = n - 2;
  else if (p === 'light' && a === 'heavy') stressIndex = n - 3;
  else stressIndex = n - 2;
  return { syllables, weights, stressIndex, manual: false };
}

const ENDS_IN_C = new RegExp(`${C}$`, 'u');
const NONSYLLABIC_G = new RegExp(NONSYLLABIC, 'g');
const DIPHTHONG_G = new RegExp(DIPHTHONG, 'g');

function syllWeight(s: string): Weight {
  if (ENDS_IN_C.test(s) || s.endsWith('ː')) return 'heavy';
  if (s.endsWith(NONSYLLABIC)) return 'heavy';
  return 'light';
}

function cosmetic(s: string): string {
  return s
    .replace(/ṛ/g, 'r̥')
    .replace(NONSYLLABIC_G, '')
    .replace(DIPHTHONG_G, '')
    .replace(/[áéíóúý]/g, (v) => DEACUTE[v] ?? v);
}

/**
 * Small LRU memo for the pure word → IPA functions (paradigm tables and the translator ask for
 * the same forms over and over). Bounded so a long session on a phone never grows it without
 * limit; Node tools (tests, score) may raise the bound with setIpaMemoLimit().
 */
class Lru {
  private readonly m = new Map<string, string>();
  max: number;
  constructor(max: number) {
    this.max = max;
  }
  get(k: string): string | undefined {
    const v = this.m.get(k);
    if (v !== undefined) {
      this.m.delete(k);
      this.m.set(k, v);
    }
    return v;
  }
  set(k: string, v: string): void {
    if (this.max <= 0) return;
    this.m.set(k, v);
    while (this.m.size > this.max) this.m.delete(this.m.keys().next().value as string);
  }
  clear(): void {
    this.m.clear();
  }
}

const CRUX_MEMO = new Lru(4096);
const IPA_MEMO = new Lru(4096);

/** Bound of each IPA memo (default 4096 entries; 0 turns memoisation off). */
export function setIpaMemoLimit(max: number): void {
  CRUX_MEMO.max = max;
  IPA_MEMO.max = max;
  CRUX_MEMO.clear();
  IPA_MEMO.clear();
}

/** Final stress (a command) for a word of more than one syllable; the module's index otherwise. */
const finalIndex = (r: { syllables: string[]; stressIndex: number | null; manual: boolean }, final: boolean): number | null =>
  final && !r.manual && r.stressIndex !== null && r.syllables.length > 1 ? r.syllables.length - 1 : r.stressIndex;

/** The module's crux() for one space-free word: phone string with ˈ inserted (`final`: a command). */
function crux(word: string, final = false): string {
  const key = final ? `\u0000${word}` : word; // a separate memo entry for the command reading
  const hit = CRUX_MEMO.get(key);
  if (hit !== undefined) return hit;
  const r = cruxRaw(word, final);
  CRUX_MEMO.set(key, r);
  return r;
}

function cruxRaw(word: string, final: boolean): string {
  const { t } = applyRules(word);
  const r = writeStress(t);
  const { syllables } = r;
  const stressIndex = finalIndex(r, final);
  if (stressIndex === null) return syllables.join('');
  const out = [...syllables];
  out.splice(stressIndex, 0, 'ˈ');
  return out.join('');
}

function postClassicalCrux(word: string): string {
  let t = crux(word);
  t = gsub(t, `sri(${VONLY})`, 'ɟ%1');
  t = gsub(t, `sˈri(${VONLY})`, 'ˈɟ%1');
  t = gsub(t, 'sr', 'ɟ');
  t = gsub(t, 'sˈr', 'ˈɟ');
  return t;
}

/**
 * Classical IPA exactly as the wiki's {{hv-IPA}} prints it between slashes, e.g.
 * ipa("ābra") === "ˈaːbra", ipa("zaldrīzes") === "zalˈdriːzes". Words are split on single
 * spaces like the module does; a lemma with an explicit {{hv-IPA|…}} argument should pass
 * that argument instead of the headword.
 */
export function ipa(text: string, opts: { final?: readonly boolean[] } = {}): string {
  // memoised on the plain path (no command words), which is nearly every call
  const plain = !opts.final?.some(Boolean);
  const hit = plain ? IPA_MEMO.get(text) : undefined;
  if (hit !== undefined) return hit;
  const r = cosmetic(
    text
      .split(' ')
      .map((w, i) => crux(w, !!opts.final?.[i]))
      .join(' '),
  );
  if (plain) IPA_MEMO.set(text, r);
  return r;
}

/**
 * Post-Classical IPA (sr → ɟ) when the wiki would print a second line, otherwise null.
 * The module compares crux() of the whole term, so a phrase only gets the line when it differs.
 */
export function ipaPostClassical(text: string): string | null {
  const term = text;
  if (crux(term) === postClassicalCrux(term)) return null;
  return cosmetic(term.split(' ').map(postClassicalCrux).join(' '));
}

/** Syllables, weights and stress of one word, in the module's own analysis. */
export function analyzeStress(word: string): StressAnalysis {
  const { t } = applyRules(word);
  const r = writeStress(t);
  return {
    rawSyllables: r.syllables,
    ipaSyllables: r.syllables.map(cosmetic),
    weights: r.weights,
    stressIndex: r.stressIndex,
    manual: r.manual,
  };
}

/** IPA syllables of one word (no stress mark), as the module divides them. */
export function syllabify(word: string): string[] {
  return analyzeStress(word).ipaSyllables;
}

export interface StressDisplay {
  /** Orthographic syllables of the input word, e.g. ["zal", "drī", "zes"]. */
  syllables: string[];
  /** Stressed syllable index, or null (monosyllable / affix: the wiki marks no stress). */
  stressed: number | null;
  /** Ready-made display string, e.g. "zal·DRĪ·zes" (monosyllables stay lowercase). */
  display: string;
  weights: Weight[];
}

/**
 * Map the module's syllabification back onto the spelling of the word, for UI such as
 * "zal·DRĪ·zes". Works on any inflected form; stress is recomputed from the surface form.
 * Case of the input is preserved (only the stressed syllable is uppercased in `display`).
 */
export function stressDisplay(word: string, opts: { final?: boolean } = {}): StressDisplay {
  const w = word.normalize('NFC');
  const tracked = applyRules(w);
  const r0 = writeStress(tracked.t);
  const r = { ...r0, stressIndex: finalIndex(r0, !!opts.final) };
  // boundaries in the phone string (writeStress only inserts/moves dots, never changes phones)
  const owner = new Array<number>(w.length).fill(-1);
  let pos = 0;
  r.syllables.forEach((syl, si) => {
    for (let k = 0; k < syl.length; k++) {
      const src = tracked.src[pos + k];
      if (src !== undefined && owner[src] === -1) owner[src] = si;
    }
    pos += syl.length;
  });
  // characters that produced no phone (the h of rh/th/…) or were merged go with the previous one
  for (let i = 0; i < owner.length; i++) {
    if (owner[i] === -1) owner[i] = i > 0 ? owner[i - 1]! : 0;
  }
  // keep syllable owners monotonic (a later character can never belong to an earlier syllable)
  for (let i = 1; i < owner.length; i++) if (owner[i]! < owner[i - 1]!) owner[i] = owner[i - 1]!;
  let syllables: string[] = r.syllables.map(() => '');
  for (let i = 0; i < w.length; i++) syllables[owner[i]!] += w[i]!;
  let stressed = r.stressIndex;
  let weights = r.weights;
  // the module treats a hyphen or other non-letters as a syllable of its own ("-ys" → "-", "ys");
  // for display, glue vowelless pieces to the next syllable (or the previous one at the end)
  if (syllables.some((s) => !/[aeiouyāēīōūȳáéíóúý]/i.test(s)) && syllables.length > 1) {
    const merged: string[] = [];
    const mw: Weight[] = [];
    let carry = '';
    syllables.forEach((s, i) => {
      if (!/[aeiouyāēīōūȳáéíóúý]/i.test(s)) {
        if (stressed !== null && i < stressed) stressed--;
        if (i === syllables.length - 1 && merged.length) merged[merged.length - 1] += s;
        else carry += s;
        return;
      }
      merged.push(carry + s);
      mw.push(r.weights[i]!);
      carry = '';
    });
    if (carry) {
      if (merged.length) merged[merged.length - 1] += carry;
      else {
        merged.push(carry);
        mw.push('light');
      }
    }
    syllables = merged;
    weights = mw;
  }
  const display = syllables.map((s, i) => (i === stressed ? s.toUpperCase() : s)).join('·');
  return { syllables, stressed, display, weights };
}

/** stressDisplay for each space-separated word of a phrase (`final`: per word, a command). */
export function stressDisplayText(text: string, opts: { final?: readonly boolean[] } = {}): StressDisplay[] {
  return text
    .split(/\s+/)
    .filter(Boolean)
    .map((w, i) => stressDisplay(w, { final: !!opts.final?.[i] }));
}
